<script setup>
import { computed, defineProps, inject, onMounted, ref } from "vue";
import FilterAsc from "@/components/icons/FilterAsc.vue";
import FilterDesc from "@/components/icons/FilterDesc.vue";
import TagsPagination from "@/components/table/TagsPagination.vue";

const props = defineProps({
  columnList: {
    type: Array,
    required: true
  },
  useMultiSelect: {
    type: Boolean,
    default: true
  },
  items: {
    type: Array,
    required: true,
    default: () => []
  }
});

onMounted(() => {
  selectedItems.value = [];
  editedItems.value = [];
});


// Активная строка, записывается при клике на нее.
const activeRow = ref({
  column: null,
  row: null
});

// Строка, на которую в данный момент наведен курсор
const hoverRow = ref({
  column: null,
  row: null
});

const activePage = ref(1);

const itemsPerPage = 10;

const totalPages = computed(() =>
  Math.ceil(props.items.length / itemsPerPage)
);

const paginatedItems = computed(() => {
  const start = (activePage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return props.items.slice(start, end);
});

const handlePageChange = (page) => {
  activePage.value = page;
};

// Проверка, является ли колонка редактируемой
const isColumnEditable = (columnName) => {
  return props.columnList.filter((col) => col.id === columnName)[0]?.isEdit;
};

// Получение ширины колонки
const getColumnWidth = (columnName) => {
  return props.columnList.filter((col) => col.id === columnName)[0]?.width;
};

// Заголовок колонки столбца, на которой наведен курсор
const hoverColumnTitle = computed(() => {
  return findMatchingPair(hoverRow.value);
});

// Заголовок колонки столбца, которая является активной. (При клике)
const activeColumnTitle = computed(() => {
  return findMatchingPair(activeRow.value);
});


function findMatchingPair(data) {
  const { column, row } = data;

  if (row === null) return null;

  for (const [key, value] of Object.entries(row)) {
    if (value === column) {
      return key;
    }
  }

  return null;
}

// Список элементов, которые были отредактированы.
const editedItems = inject("editedItems");

// Элементы в таблице, которые отмечены чекбоксом.
const selectedItems = inject("selectedItems");

// Выбор всех строк
const toggleAllRows = (status) => {
  if (status) {
    selectedItems.value = props.items;
  } else {
    selectedItems.value = [];
  }
};
// Выбор 1 строки
const toggleSelectedRow = (status, row) => {
  if (status) {
    selectedItems.value.push(row);
  } else {
    selectedItems.value.splice(selectedItems.value.indexOf(row), 1);
  }
};

// Сортировка по колонкам

function sortByColumnUp(array, key) {
  return array.sort((a, b) => {
    if (a[key] < b[key]) {
      return -1;
    }
    if (a[key] > b[key]) {
      return 1;
    }
    return 0;
  });
}

function sortByColumnDown(array, key) {
  return array.sort((a, b) => {
    if (a[key] > b[key]) {
      return -1;
    }
    if (a[key] < b[key]) {
      return 1;
    }
    return 0;
  });
}

const filterByColumn = (column, direction) => {
  direction ? column.filtered = true : column.filtered = false;
  direction ? sortByColumnDown(props.items, column.id) : sortByColumnUp(props.items, column.id); 
};

function objReverse(obj) {
  const new_obj = {};
  const rev_obj = Object.keys(obj).reverse();
  rev_obj.forEach(function(i) { 
    new_obj[i] = obj[i];
  });
  return new_obj;
}

</script>

<template>
  <div>
    <div
      class="table-wrapper scroll"
    >
      <table class="table">
        <thead class="table-header">
          <tr class="table-header-row">
            <th
              v-if="props.useMultiSelect"
              style="width: 60px"
            >
              <div class="table-header-row-column table-header-row__multiselect">
                <input
                  type="checkbox"
                  @input="(state) => toggleAllRows(state.target.checked)"
                >
              </div>
            </th>
            <th
              v-for="title in props.columnList"
              :key="title"
              :class="{'table-header-row-column__active': title.id === activeColumnTitle}"
              :style="{'width': title.width ? title.width : 'auto'}"
            >
              <div
                class="table-header-row-column"
              >
                {{ title.name }}
                <div class="filter">
                  <FilterDesc
                    v-if="title.filtered"
                    class="filter__icon"
                    src="@/assets/icons/filter.svg"
                    alt="filter"
                    @click="filterByColumn(title, false)"
                  />
                  <FilterAsc
                    v-else
                    class="filter__icon"
                    src="@/assets/icons/filter.svg"
                    alt="filter"
                    @click="filterByColumn(title, true)"
                  />
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody
          class="table-body"
        >
          <tr
            v-for="(row) in paginatedItems"
            :key="row"
            class="table-body-row"
          >
            <td
              v-if="props.useMultiSelect"
              :class="{'table-body-row-column__active-background': row === activeRow.row}"
            >
              <div
                class="table-body-row-column table-body-row-column__multiselect"
              >
                <input
                  type="checkbox"
                  :checked="selectedItems.includes(row)"
                  @input="(state) => toggleSelectedRow(state.target.checked, row)"
                >
              </div>
            </td>
            <td
              v-for="(column, columnName) in objReverse(row)"
              :key="columnName"
              :class="{
                'table-body-row-column__active-border': activeRow.column === column && activeRow.row === row,
                'table-body-row-column__hover-background': hoverColumnTitle === columnName
              }"
              :style="{'max-width': getColumnWidth(columnName) ? getColumnWidth(columnName) : 'auto'}"
              @click="activeRow = {column: column, row: row}"
              @dblclick="if (isColumnEditable(columnName)) {
                const existingIndex = editedItems.findIndex((item) => item.id === row.id);
                if (existingIndex === -1) {
                  editedItems.push({ ...row });
                } else {
                  editedItems[existingIndex] = { ...row };
                }
              }"

              @mouseenter="hoverRow = {column: column, row: row}"
              @mouseleave="hoverRow = {column: null, row: null}"
            >
              <div
                v-if="editedItems.includes(row)"
                class="table-body-row-column"
              >
                <input
                  v-if="isColumnEditable(columnName)"
                  v-model="editedItems[editedItems.findIndex((item) => item.id === row.id)][columnName]"
                  type="text"
                >
                <div v-else>
                  {{ editedItems[editedItems.findIndex((item) => item.id === row.id)][columnName] }}
                </div>
              </div>
              <div
                v-else
                class="table-body-row-column"
              >
                {{ column }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <TagsPagination
      :active-page="activePage"
      :total-pages="totalPages"
      @page-change="handlePageChange"
    />
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/styles/vars.scss' as *;

.table-wrapper {
  border-radius: 16px;
}


.table {
  width: 100%;

  &-header {
    position: sticky;
    top: 0;
    height: 55px;
    color: $table-header-text;
    background: $table-header-background;

    &-row {
      &-column {
        font-weight: normal;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 24px 0 24px;
        gap: 20px;

        .filter {
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 100px;
          width: 32px;
          height: 32px;
          cursor: pointer;

          &__icon {
            width: 20px;
            height: 20px;
          }

          &:hover {
            background: #ffffff;
          }
        }

        &__active {
          background: #EEF1FF;
        }
      }

      &__multiselect {
        justify-content: center;
      }
    }
  }

  &-body {
    background: $table-body-background;

    &-row {
      td {
        padding: 16px 24px 16px 24px;

        &:hover {
          outline: 1px solid #7B87A5;
        }
      }

      &:hover {
        background: #F7F8FD;
      }

      &-column {
        display: -webkit-box;
        overflow: hidden;
        max-height: 60px;
        text-overflow: ellipsis;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        width: 100%;

        &__hover-background {
          background: #F7F8FD;
        }

        &__active-background {
          background: #EEF1FF;
        }

        &__active-border {
          outline: 1px solid #1F9AFF !important;
        }

        &__multiselect {
          display: flex;
          justify-content: center;
        }

        input[type=text] {
          all: unset;
          width: 100%;
        }

      }
    }
  }
}

</style>